import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
    Receipt,
    ArrowLeft,
    Edit,
    Calculator,
    CheckCircle,
    XCircle,
    Clock,
    DollarSign,
    Calendar,
    Building,
    Loader2,
    AlertCircle,
    RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { payoutService } from '@/services/payoutService'
import { PaymentPayoutBatch, PayoutLineItem } from '@/types'
import { safeFormatDistanceToNow } from '@/utils/date'

export default function AdminPayoutBatchDetail() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [batch, setBatch] = useState<PaymentPayoutBatch | null>(null)
    const [lineItems, setLineItems] = useState<PayoutLineItem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [actionLoading, setActionLoading] = useState<string | null>(null)

    useEffect(() => {
        if (id) {
            fetchBatchData()
        }
    }, [id])

    const fetchBatchData = async () => {
        if (!id) return

        try {
            setLoading(true)
            setError(null)

            const [batchResponse, lineItemsResponse] = await Promise.all([
                payoutService.getPayoutBatch(parseInt(id)),
                payoutService.getPayoutBatchLineItems(parseInt(id))
            ])

            if (batchResponse.success) {
                setBatch(batchResponse.data)
            } else {
                setError('Failed to fetch payout batch')
            }

            if (lineItemsResponse.success) {
                setLineItems(lineItemsResponse.data)
            }
        } catch (err) {
            setError('An error occurred while fetching batch data')
            console.error('Error fetching batch data:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleBatchAction = async (action: string) => {
        if (!id) return

        setActionLoading(action)
        try {
            let response
            switch (action) {
                case 'calculate':
                    response = await payoutService.calculatePayoutBatchTotals(parseInt(id))
                    break
                case 'build_line_items':
                    response = await payoutService.buildPayoutBatchLineItems(parseInt(id))
                    break
                case 'approve':
                    response = await payoutService.approvePayoutBatch(parseInt(id))
                    break
                case 'mark_paid':
                    response = await payoutService.markPayoutBatchPaid(parseInt(id))
                    break
            }

            if (response?.success) {
                await fetchBatchData()
            }
        } catch (err) {
            console.error(`Error performing ${action} on batch:`, err)
        } finally {
            setActionLoading(null)
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'draft':
                return 'text-gray-600 bg-gray-100 border-gray-200'
            case 'calculated':
                return 'text-blue-600 bg-blue-100 border-blue-200'
            case 'approved':
                return 'text-green-600 bg-green-100 border-green-200'
            case 'paid':
                return 'text-green-700 bg-green-200 border-green-300'
            case 'cancelled':
                return 'text-red-600 bg-red-100 border-red-200'
            default:
                return 'text-gray-600 bg-gray-100 border-gray-200'
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'draft':
                return Clock
            case 'calculated':
                return Calculator
            case 'approved':
                return CheckCircle
            case 'paid':
                return CheckCircle
            case 'cancelled':
                return XCircle
            default:
                return Clock
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: branding.primaryColor }} />
                    <p style={pageStyles.textSecondary}>Loading payout batch...</p>
                </div>
            </div>
        )
    }

    if (error || !batch) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <AlertCircle className="h-8 w-8 mx-auto mb-4" style={{ color: statusColors.error.color }} />
                    <p className="mb-4" style={{ color: statusColors.error.color }}>{error || 'Payout batch not found'}</p>
                    <Button
                        onClick={() => navigate('/admin/payout-batches')}
                        variant="outline"
                        style={{
                            color: branding.textSecondaryColor,
                            borderColor: `${branding.textSecondaryColor}40`,
                            backgroundColor: 'transparent'
                        }}
                        className="hover:opacity-80"
                    >
                        Back to Batches
                    </Button>
                </div>
            </div>
        )
    }

    const StatusIcon = getStatusIcon(batch.status)

    return (
        <div className="min-h-screen" style={pageStyles.container}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/admin/payout-batches')}
                                style={{ color: branding.textSecondaryColor }}
                                className="hover:opacity-80"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                            </Button>
                            <div>
                                <h1 className="text-3xl font-bold" style={pageStyles.text}>Payout Batch #{batch.id}</h1>
                                <p className="mt-1" style={pageStyles.textSecondary}>{batch.merchant_name}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(batch.status)}`}>
                                <StatusIcon className="h-4 w-4 mr-2" />
                                {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                            </span>
                            <Button
                                onClick={() => navigate(`/admin/payout-batches/${batch.id}/edit`)}
                                variant="outline"
                                className="flex items-center hover:opacity-80"
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                            >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Batch Information */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Basic Information */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h2 className="text-lg font-semibold mb-4" style={pageStyles.text}>Batch Information</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>Merchant</label>
                                    <div className="mt-1 flex items-center">
                                        <Building className="h-4 w-4 mr-2" style={{ color: branding.textSecondaryColor }} />
                                        <p className="text-sm" style={pageStyles.textSecondary}>{batch.merchant_name}</p>
                                    </div>
                                </div>
                                {batch.submerchant_name && (
                                    <div>
                                        <label className="text-sm font-medium" style={pageStyles.textSecondary}>Submerchant</label>
                                        <p className="mt-1 text-sm" style={pageStyles.textSecondary}>{batch.submerchant_name}</p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>Period Start</label>
                                    <div className="mt-1 flex items-center">
                                        <Calendar className="h-4 w-4 mr-2" style={{ color: branding.textSecondaryColor }} />
                                        <p className="text-sm" style={pageStyles.textSecondary}>{new Date(batch.period_start).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>Period End</label>
                                    <div className="mt-1 flex items-center">
                                        <Calendar className="h-4 w-4 mr-2" style={{ color: branding.textSecondaryColor }} />
                                        <p className="text-sm" style={pageStyles.textSecondary}>{new Date(batch.period_end).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>Currency</label>
                                    <p className="mt-1 text-sm" style={pageStyles.textSecondary}>{batch.currency}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>Created</label>
                                    <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                        {safeFormatDistanceToNow(batch.created_at, 'Unknown', { addSuffix: true })}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Financial Summary */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h2 className="text-lg font-semibold mb-4" style={pageStyles.text}>Financial Summary</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="text-center p-4 rounded-lg" style={{ backgroundColor: `${branding.textSecondaryColor}20` }}>
                                    <div className="flex items-center justify-center mb-2">
                                        <Receipt className="h-6 w-6" style={{ color: branding.textSecondaryColor }} />
                                    </div>
                                    <p className="text-sm font-medium" style={pageStyles.textSecondary}>Total Transactions</p>
                                    <p className="text-2xl font-bold" style={pageStyles.text}>{batch.total_transactions}</p>
                                </div>
                                <div className="text-center p-4 rounded-lg" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                    <div className="flex items-center justify-center mb-2">
                                        <DollarSign className="h-6 w-6" style={{ color: branding.primaryColor }} />
                                    </div>
                                    <p className="text-sm font-medium" style={pageStyles.textSecondary}>Gross Amount</p>
                                    <p className="text-2xl font-bold" style={pageStyles.text}>${Number(batch.gross_amount_fiat).toFixed(2)}</p>
                                </div>
                                <div className="text-center p-4 rounded-lg" style={{ backgroundColor: `${branding.secondaryColor}20` }}>
                                    <div className="flex items-center justify-center mb-2">
                                        <Calculator className="h-6 w-6" style={{ color: branding.secondaryColor }} />
                                    </div>
                                    <p className="text-sm font-medium" style={pageStyles.textSecondary}>Commission</p>
                                    <p className="text-2xl font-bold" style={pageStyles.text}>${Number(batch.commission_amount_fiat).toFixed(2)}</p>
                                </div>
                            </div>
                            <div className="mt-6 text-center p-4 rounded-lg" style={{ backgroundColor: `${statusColors.success.backgroundColor}20` }}>
                                <div className="flex items-center justify-center mb-2">
                                    <DollarSign className="h-6 w-6" style={{ color: statusColors.success.color }} />
                                </div>
                                <p className="text-sm font-medium" style={pageStyles.textSecondary}>Net Amount (Payout)</p>
                                <p className="text-3xl font-bold" style={pageStyles.text}>${Number(batch.net_amount_fiat).toFixed(2)} {batch.currency}</p>
                            </div>
                        </div>

                        {/* Line Items */}
                        <div className="border backdrop-blur-md shadow-sm" style={cardStyles}>
                            <div className="p-6 border-b" style={{ borderColor: `${branding.textSecondaryColor}30` }}>
                                <div className="flex items-center justify-between">
                                    <h2 className="text-lg font-semibold" style={pageStyles.text}>Transaction Line Items</h2>
                                    <Button
                                        onClick={() => handleBatchAction('build_line_items')}
                                        disabled={actionLoading === 'build_line_items'}
                                        variant="outline"
                                        size="sm"
                                        style={{
                                            color: branding.textSecondaryColor,
                                            borderColor: `${branding.textSecondaryColor}40`,
                                            backgroundColor: 'transparent'
                                        }}
                                        className="hover:opacity-80"
                                    >
                                        {actionLoading === 'build_line_items' ? (
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <RefreshCw className="h-4 w-4 mr-2" />
                                        )}
                                        Rebuild Line Items
                                    </Button>
                                </div>
                            </div>
                            <div className="p-6">
                                {lineItems.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Receipt className="h-12 w-12 mx-auto mb-4" style={{ color: branding.textSecondaryColor }} />
                                        <p style={pageStyles.textSecondary}>No line items found</p>
                                        <Button
                                            onClick={() => handleBatchAction('build_line_items')}
                                            className="mt-4 hover:opacity-80"
                                            variant="outline"
                                            style={{
                                                color: branding.textSecondaryColor,
                                                borderColor: `${branding.textSecondaryColor}40`,
                                                backgroundColor: 'transparent'
                                            }}
                                        >
                                            Build Line Items
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y" style={{ borderColor: `${branding.textSecondaryColor}30` }}>
                                            <thead style={{ backgroundColor: `${branding.backgroundColor}80` }}>
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                                        Transaction
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                                        Amount
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                                        Commission Rate
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                                        Commission Amount
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                                        Net Amount
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y" style={{
                                                backgroundColor: `${branding.surfaceColor}30`,
                                                borderColor: `${branding.textSecondaryColor}30`
                                            }}>
                                                {lineItems.map((item) => (
                                                    <tr key={item.id}>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium" style={pageStyles.text}>#{item.transaction}</div>
                                                            <div className="text-sm font-mono" style={pageStyles.textSecondary}>{item.transaction_reference}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.textSecondary}>
                                                            ${Number(item.transaction_amount).toFixed(2)} {item.transaction_currency}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.textSecondary}>
                                                            {Number(item.commission_rate).toFixed(2)}%
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.textSecondary}>
                                                            ${Number(item.commission_amount).toFixed(2)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.textSecondary}>
                                                            ${Number(item.net_amount).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Actions Sidebar */}
                    <div className="space-y-6">
                        {/* Batch Actions */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h3 className="text-lg font-semibold mb-4" style={pageStyles.text}>Batch Actions</h3>
                            <div className="space-y-3">
                                {batch.status === 'draft' && (
                                    <Button
                                        onClick={() => handleBatchAction('calculate')}
                                        disabled={actionLoading === 'calculate'}
                                        className="w-full flex items-center justify-center"
                                        style={pageStyles.primaryBg}
                                    >
                                        {actionLoading === 'calculate' ? (
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Calculator className="h-4 w-4 mr-2" />
                                        )}
                                        Calculate Totals
                                    </Button>
                                )}
                                {batch.status === 'calculated' && (
                                    <Button
                                        onClick={() => handleBatchAction('approve')}
                                        disabled={actionLoading === 'approve'}
                                        className="w-full flex items-center justify-center"
                                        style={{ backgroundColor: statusColors.success.backgroundColor }}
                                    >
                                        {actionLoading === 'approve' ? (
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <CheckCircle className="h-4 w-4 mr-2" />
                                        )}
                                        Approve Batch
                                    </Button>
                                )}
                                {batch.status === 'approved' && (
                                    <Button
                                        onClick={() => handleBatchAction('mark_paid')}
                                        disabled={actionLoading === 'mark_paid'}
                                        className="w-full flex items-center justify-center"
                                        style={{ backgroundColor: statusColors.success.backgroundColor }}
                                    >
                                        {actionLoading === 'mark_paid' ? (
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <CheckCircle className="h-4 w-4 mr-2" />
                                        )}
                                        Mark as Paid
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Batch Status */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h3 className="text-lg font-semibold mb-4" style={pageStyles.text}>Status Information</h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm" style={pageStyles.textSecondary}>Current Status</span>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(batch.status)}`}>
                                        <StatusIcon className="h-3 w-3 mr-1" />
                                        {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm" style={pageStyles.textSecondary}>Created</span>
                                    <span className="text-sm" style={pageStyles.textSecondary}>
                                        {safeFormatDistanceToNow(batch.created_at, 'Unknown', { addSuffix: true })}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm" style={pageStyles.textSecondary}>Last Updated</span>
                                    <span className="text-sm" style={pageStyles.textSecondary}>
                                        {safeFormatDistanceToNow(batch.updated_at, 'Unknown', { addSuffix: true })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
